import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { BACKEND_URI } from "../config/config-vars";
import { firstValueFrom, Observable } from "rxjs";
import { BoardGameStats } from "../models/boardGameStats.model";
import { GameAggregateStats } from "../models/game-aggregate-stats.model";
import { GameStatsBreakdown } from "../models/game-stats-breakdown.model";
import { GameBoardBreakdown } from "../models/game-board-breakdown.model";
import { GameStatsDetails } from "../models/game-stats-details.model";

const BASE_URL = `${BACKEND_URI}/api/board-game-stats`;
const GET_TOP_GAMES_URL = `${BASE_URL}/top-games`;
const GET_LIVE_BY_BOARD_DAY_URL = `${BASE_URL}/live/by-board-day`;

// ✅ Correct path to get ALL board game stats for a given boardStats UUID
const GET_BY_BOARD_STATS_URL = (boardStatsId: string) =>
  `${BASE_URL}/by-board-stats/${encodeURIComponent(boardStatsId)}`;

// ✅ Optional: composite key lookup for exact stat
const GET_BY_COMPOSITE_ID_URL = (boardStatsId: string, gameId: number) => {
  const params = new URLSearchParams({
    boardStatsId,
    gameId: gameId.toString()
  });
  return `${BASE_URL}/by-composite-id?${params.toString()}`;
};

@Injectable({ providedIn: "root" })
export class BoardGameStatsService {
  constructor(private http: HttpClient) {}

  /**
   * ✅ Main method used by frontend modal
   * Get all board game stats associated with a boardStatsId
   */
  async getByBoardStatsId(boardStatsId: string): Promise<BoardGameStats[]> {
    try {
      const url = GET_BY_BOARD_STATS_URL(boardStatsId);
      return await firstValueFrom(this.http.get<BoardGameStats[]>(url));
    } catch (error) {
      console.error("Failed to fetch board game stats", error);
      return [];
    }
  }

  /**
   * Optional: Get a specific board game stat by composite key: boardStatsId + gameId
   */
  async getByCompositeId(boardStatsId: string, gameId: number): Promise<BoardGameStats | null> {
    try {
      const url = GET_BY_COMPOSITE_ID_URL(boardStatsId, gameId);
      return await firstValueFrom(this.http.get<BoardGameStats>(url));
    } catch (error) {
      console.error("Failed to fetch by composite ID", error);
      return null;
    }
  }

  async getLiveByBoardDay(params: {
    boardId: string;
    date: string;
    cashIn?: number;
    cashOut?: number;
    coinIn?: number;
    coinOut?: number;
    gamesPlayed?: number;
    gamesWon?: number;
  }): Promise<BoardGameStats[]> {
    try {
      let httpParams = new HttpParams()
        .set('boardId', params.boardId)
        .set('date', params.date);

      if (params.cashIn !== undefined) httpParams = httpParams.set('cashIn', String(params.cashIn));
      if (params.cashOut !== undefined) httpParams = httpParams.set('cashOut', String(params.cashOut));
      if (params.coinIn !== undefined) httpParams = httpParams.set('coinIn', String(params.coinIn));
      if (params.coinOut !== undefined) httpParams = httpParams.set('coinOut', String(params.coinOut));
      if (params.gamesPlayed !== undefined) httpParams = httpParams.set('gamesPlayed', String(params.gamesPlayed));
      if (params.gamesWon !== undefined) httpParams = httpParams.set('gamesWon', String(params.gamesWon));

      const rows = await firstValueFrom(this.http.get<any[]>(GET_LIVE_BY_BOARD_DAY_URL, { params: httpParams }));

      return (rows || []).map((row, index) => ({
        gameStatsId: {
          boardStatsId: row.boardStatsId || row.boardReadId || params.boardId,
          gameId: Number(row.externalGameId || row.gameId || index + 1),
        },
        game: {
          id: Number(row.gameId || index + 1),
          gameId: Number(row.externalGameId || row.gameId || index + 1),
          gameName: row.gameName || 'Unknown',
        },
        coinInDiff: Number(row.coinIn || 0),
        coinOutDiff: Number(row.coinOut || 0),
        gamePlayedDiff: Number(row.gamesPlayed || 0),
        gameWonDiff: Number(row.gamesWon || 0),
      })) as BoardGameStats[];
    } catch (error) {
      console.error('Failed to fetch live board game stats', error);
      return [];
    }
  }

getTopGames(startDate: string, endDate: string, salesRepId?: string): Promise<GameAggregateStats[]> {
  let params = new HttpParams()
    .set('startDate', startDate)
    .set('endDate', endDate);

  if (salesRepId) {
    params = params.set('salesRepId', salesRepId);
  }

  return firstValueFrom(
    this.http.get<GameAggregateStats[]>(GET_TOP_GAMES_URL, { params })
  );
}


getGameBreakdown(gameName: string, startDate: string, endDate: string): Observable<GameStatsBreakdown[]> {
  const params = new HttpParams()
    .set('gameName', gameName)
    .set('startDate', startDate)
    .set('endDate', endDate);

  return this.http.get<GameStatsBreakdown[]>(`${BASE_URL}/breakdown`, { params });
}

getBoardSummary(gameName: string, startDate: string, endDate: string) {
  return this.http.get<GameBoardBreakdown[]>(
    `${BASE_URL}/board-summary`,
    {
      params: { gameName, startDate, endDate },
    }
  );
}

getDetailedBreakdown(gameName: string, startDate: string, endDate: string) {
  return this.http.get<GameStatsDetails[]>(
    `${BASE_URL}/detailed-breakdown`,
    {
      params: { gameName, startDate, endDate },
    }
  );
}





}

